import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, ArrowRight, X } from 'lucide-react';
import notificationService from '../services/notificationService';

const NotificationsBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications({ unread: true });
      const newNotifications = response.data || [];

      // Se há notificações não lidas, ativa o efeito de pulsação
      setHasUnread(newNotifications.length > 0);

      setNotifications(newNotifications);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();

    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fechar ao clicar fora no computador
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications([]);
      setHasUnread(false);
    } catch (error) {
      console.error('Erro ao marcar notificações como lidas:', error);
    }
  };

  const unreadCount = notifications.length;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleOpen}
        className={`relative p-2 rounded-full hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${hasUnread ? 'animate-bounce' : ''}`}
        title="Notificações"
      >
        <Bell className={`h-5 w-5 text-secondary-600 ${hasUnread ? 'text-red-600' : ''}`} />
        {unreadCount > 0 && (
          <span className={`absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white ${hasUnread ? 'animate-pulse' : ''}`}>
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden" onClick={() => setOpen(false)} />
          <div className="fixed left-2 right-2 bottom-2 top-auto max-h-[85vh] bg-white rounded-xl shadow-2xl z-50 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:bottom-auto sm:w-96 sm:max-w-md sm:max-h-[30rem] sm:mt-2 sm:rounded-lg sm:shadow-lg ring-1 ring-black ring-opacity-5 flex flex-col overflow-hidden">
            <div className="p-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <span className="text-sm font-medium text-gray-900">Notificações</span>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllAsRead}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <Check className="w-3 h-3" />
                    <span className="hidden sm:inline">Marcar todas como lidas</span>
                    <span className="sm:hidden">Ler</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                  title="Fechar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="p-4 text-sm text-gray-500 text-center">Carregando...</div>
              )}
              {!loading && notifications.length === 0 && (
                <div className="p-4 text-sm text-gray-500 text-center">
                  Nenhuma notificação pendente.
                </div>
              )}
              {!loading && notifications.length > 0 && (
                <ul className="divide-y divide-gray-100">
                  {notifications.map((n) => (
                    <li key={n.id} className="p-2.5 sm:p-3 text-sm flex flex-col gap-1.5 break-words">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-xs sm:text-sm break-words">{n.title}</p>
                        <p className="text-gray-600 text-xs mt-0.5 break-words line-clamp-2">{n.message}</p>
                        <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 break-words">
                          {new Date(n.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      {/* 1. Levantamento */}
                      {n.entity_type === 'survey' && n.entity_id && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate(`/levantamentos?surveyId=${n.entity_id}`);
                            setOpen(false);
                          }}
                        >
                          Ver Detalhes
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}

                      {/* 2. Agendamento */}
                      {n.entity_type === 'appointment' && n.entity_id && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate(`/agendamentos/editar/${n.entity_id}`);
                            setOpen(false);
                          }}
                        >
                          Ver Agendamento
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}

                      {/* 3. Horas Extras */}
                      {n.entity_type === 'overtime_monthly_report' && n.entity_id && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate(`/users/horas-extras?reportId=${n.entity_id}`);
                            setOpen(false);
                          }}
                        >
                          Ver Detalhes
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}

                      {/* 4. Holerite / Folha */}
                      {(n.entity_type === 'payslip' || n.type === 'PAYSLIP_APPROVED') && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800 font-medium"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            // Redireciona diretamente para a tela de holerites
                            navigate(n.entity_id ? `/meus-holerites?id=${n.entity_id}` : '/meus-holerites');
                            setOpen(false);
                          }}
                        >
                          Ver Holerite
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}

                      {/* 5. Adiantamentos */}
                      {(n.entity_type === 'advance_request' || n.type === 'ADVANCE_APPROVED') && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800 font-medium"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate('/adiantamento');
                            setOpen(false);
                          }}
                        >
                          Ver Adiantamento
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}

                      {/* 6. Férias */}
                      {(n.entity_type === 'vacation_payslip' || n.type === 'VACATION_PAYSLIP_APPROVED') && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800 font-medium"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate('/users/ferias');
                            setOpen(false);
                          }}
                        >
                          Ver Férias
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}

                      {/* 7. Projeto Oficial */}
                      {(n.entity_type === 'project' || n.type === 'PROJECT_APPROVED') && n.entity_id && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800 font-medium"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate(`/projetos/${n.entity_id}`);
                            setOpen(false);
                          }}
                        >
                          Ver Projeto
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      {n.entity_type === 'expense' && n.entity_id && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate(`/users/despesas-diversas?highlight=${n.entity_id}`);
                            setOpen(false);
                          }}
                        >
                          Ver Detalhes
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      {n.entity_type === 'profit_sharing' && n.entity_id && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-blue-600 hover:text-blue-800"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate(`/users/plr?highlight=${n.entity_id}`);
                            setOpen(false);
                          }}
                        >
                          Ver Detalhes
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      {(n.type === 'recurring_cost_due' || n.type === 'recurring_cost_warning' || n.entity_type === 'recurring_cost') && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-green-600 hover:text-green-800 font-medium"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate(`/custos?costId=${n.entity_id}&openParcels=true`);
                            setOpen(false);
                          }}
                        >
                          Confirmar Pagamento
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                      {n.type === 'overdue_installment' && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 self-start text-xs text-red-600 hover:text-red-800 font-medium"
                          onClick={async () => {
                            try {
                              await notificationService.markAsRead(n.id);
                              setNotifications(prev => prev.filter(x => x.id !== n.id));
                              if (notifications.length <= 1) setHasUnread(false);
                            } catch (e) { /* ignore */ }
                            navigate(`/projetos/${n.entity_id}/financeiro?tab=saldo-inicial`);
                            setOpen(false);
                          }}
                        >
                          Ver Parcela
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsBell;


